// ===========================================
// DetailReglement — Refonte UX (header client + KPIs + collapse contrats)
// ===========================================
import React, { useEffect, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import {
    Card, Button, Row, Col, Tag, Grid, Modal, Form, InputNumber, Select, DatePicker,
    Input, Space, Typography, Divider, Progress, Table, Collapse, Tooltip, Dropdown,
} from 'antd';
import {
    ArrowLeftOutlined, DollarOutlined, UserOutlined, BankOutlined, FileProtectOutlined,
    CheckCircleOutlined, ExclamationCircleOutlined, CalendarOutlined, WalletOutlined,
    PlusOutlined, EyeOutlined, PrinterOutlined, EditOutlined, MoreOutlined,
    PhoneOutlined, RiseOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { reglementsService } from '../../common/services';
import { AuthContext } from '../../common/constants';
import { generateBordereauPdf } from '../../common/utils/bordereauPdf';
import { formatMontant, formatPhone as formatPhoneShared } from '../../common/utils/format';
import DetailSkeleton from '../../components/detailSkeleton';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './detail-reglement.page.scss';

const { Text, Title } = Typography;
const { Option } = Select;

const formatPhone = (tel) => formatPhoneShared(tel, { fallback: '' });

const MODE_CONFIG = {
    especes:      { label: 'Espèces',      color: 'green'  },
    cheque:       { label: 'Chèque',       color: 'blue'   },
    virement:     { label: 'Virement',     color: 'purple' },
    mobile_money: { label: 'Mobile Money', color: 'orange' },
};

const STATUS_CONFIG = (percent) => {
    if (percent >= 100) return { color: 'green',  label: 'Payé',    tone: 'paid' };
    if (percent > 0)    return { color: 'orange', label: 'Partiel', tone: 'partial' };
    return { color: 'red', label: 'Impayé', tone: 'unpaid' };
};

const DetailReglement = () => {
    const { id: clientId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [clientData, setClientData] = useState(null);
    const [contrats, setContrats] = useState([]);
    const [stats, setStats] = useState({});

    // Modal paiement
    const [modalVisible, setModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedContrat, setSelectedContrat] = useState(null);
    const [form] = Form.useForm();

    // Modal édition
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editModalLoading, setEditModalLoading] = useState(false);
    const [editingReglement, setEditingReglement] = useState(null);
    const [editForm] = Form.useForm();

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const { currentUser } = useContext(AuthContext);

    const fetchDetail = async () => {
        setLoading(true);
        try {
            const res = await reglementsService.etatDetailClient(clientId);
            const d = res?.data || res;
            setClientData(d?.client || null);
            setContrats(d?.contrats || []);
            setStats(d?.stats || {});
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    // ─── Impression bordereau (util partagé) ──────────────────────────
    const printBordereau = (reglement, contrat) => {
        generateBordereauPdf({
            reglement,
            contrat,
            client: clientData,
            emetteur: currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : '',
        });
        toast.success('Bordereau généré avec succès');
    };

    // ─── Édition règlement ────────────────────────────────────────────
    const openEditModal = (reglement) => {
        setEditingReglement(reglement);
        editForm.setFieldsValue({
            montant: reglement.montant,
            moyenPaiement: reglement.moyenPaiement,
            reference: reglement.reference,
            banque: reglement.banque,
            observation: reglement.observation,
        });
        setEditModalVisible(true);
    };

    const handleEditSubmit = async (values) => {
        if (!editingReglement) return;
        try {
            setEditModalLoading(true);
            await reglementsService.update(editingReglement._id, values);
            toast.success('Règlement modifié avec succès');
            setEditModalVisible(false);
            editForm.resetFields();
            setEditingReglement(null);
            fetchDetail();
        } catch (error) {
            toast.error(error.message || 'Erreur lors de la modification');
        } finally {
            setEditModalLoading(false);
        }
    };

    // ─── Suppression règlement ───────────────────────────────────────
    const handleDeleteReglement = (reglement) => {
        Modal.confirm({
            title: 'Supprimer ce règlement ?',
            icon: <ExclamationCircleOutlined style={{ color: '#C8102E' }} />,
            content: (
                <div style={{ marginTop: 8 }}>
                    <Text>
                        Vous êtes sur le point de supprimer un encaissement de{' '}
                        <Text strong>{formatMontant(reglement.montant || 0)}</Text>
                        {' '}({MODE_CONFIG[reglement.moyenPaiement]?.label || reglement.moyenPaiement}).
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Le contrat repassera automatiquement en impayé. Cette action est irréversible.
                    </Text>
                </div>
            ),
            okText: 'Supprimer',
            okButtonProps: { danger: true },
            cancelText: 'Annuler',
            onOk: async () => {
                try {
                    await reglementsService.delete(reglement._id);
                    toast.success('Règlement supprimé avec succès');
                    fetchDetail();
                } catch (error) {
                    toast.error(error.message || 'Erreur lors de la suppression');
                    throw error; // garde la modale ouverte en cas d'échec
                }
            },
        });
    };

    const openPaymentModal = (contrat) => {
        setSelectedContrat(contrat);
        form.resetFields();
        setModalVisible(true);
    };

    const handleSubmitPayment = async (values) => {
        if (!selectedContrat) return;
        try {
            setModalLoading(true);
            const res = await reglementsService.create({
                ...values,
                contrat: selectedContrat._id,
                client: clientId,
                agence: selectedContrat.agence?._id || selectedContrat.agence,
            });
            const savedReglement = res?.data || res;
            setModalVisible(false);
            form.resetFields();
            const contratForPrint = { ...selectedContrat };
            setSelectedContrat(null);
            await fetchDetail();

            Modal.success({
                title: 'Règlement enregistré avec succès',
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                content: (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ background: '#f6ffed', borderRadius: 10, padding: 16, textAlign: 'center', border: '1px solid #b7eb8f', marginBottom: 12 }}>
                            <Text type="secondary">Montant versé</Text>
                            <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a', margin: '4px 0' }}>{formatMontant(values.montant)}</div>
                            <Tag color="green">{MODE_CONFIG[values.moyenPaiement]?.label || values.moyenPaiement}</Tag>
                        </div>
                    </div>
                ),
                okText: 'Imprimer le bordereau',
                cancelText: 'Fermer',
                okButtonProps: { icon: <PrinterOutlined />, style: { background: '#1A2B5F', borderColor: '#1A2B5F' } },
                onOk: () => printBordereau({ ...savedReglement, ...values }, contratForPrint),
            });
        } catch (error) {
            toast.error(error.message || "Erreur lors de l'enregistrement");
        } finally {
            setModalLoading(false);
        }
    };

    if (loading) return <DetailSkeleton kpis={5} sections={2} />;

    if (!clientData) {
        return (
            <div className="detail-reglement-empty">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard/reglements')}>Retour</Button>
                <Card style={{ marginTop: 16, textAlign: 'center', padding: 40 }}>
                    <Title level={4}>Client non trouvé</Title>
                </Card>
            </div>
        );
    }

    const isEntreprise = clientData.type === 'entreprise';
    const clientName = `${clientData.nom || ''} ${clientData.prenom || ''}`.trim();

    // ─── Colonnes historique paiements ───────────────────────────────
    const reglementColumns = [
        {
            title: 'Date',
            key: 'date',
            width: 130,
            render: (_, r) => {
                const dateReg = r.dateReglement ? new Date(r.dateReglement) : null;
                const heureCreation = r.createdAt ? new Date(r.createdAt) : null;
                if (!dateReg) return '—';
                return (
                    <div className="reglement-date-cell">
                        <span className="date-strong">{dateReg.toLocaleDateString('fr-FR')}</span>
                        <span className="date-time">
                            {heureCreation ? heureCreation.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '00:00'}
                        </span>
                        {r.isModifie && (
                            <Tooltip title={`Modifié le ${r.dateModification ? new Date(r.dateModification).toLocaleDateString('fr-FR') : ''} par ${r.modifiePar ? r.modifiePar.firstName + ' ' + r.modifiePar.lastName : ''}`}>
                                <Tag color="orange" className="reglement-modified-tag">Modifié</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Montant',
            dataIndex: 'montant',
            key: 'montant',
            width: 140,
            align: 'right',
            render: (val) => <span className="reglement-amount is-positive">{formatMontant(val || 0)}</span>,
        },
        {
            title: 'Mode',
            dataIndex: 'moyenPaiement',
            key: 'mode',
            width: 130,
            render: (m) => {
                const cfg = MODE_CONFIG[m] || { label: m || '—', color: 'default' };
                return <Tag color={cfg.color}>{cfg.label}</Tag>;
            },
        },
        {
            title: 'Référence',
            dataIndex: 'reference',
            key: 'ref',
            width: 130,
            render: (t) => t || '—',
            responsive: ['md'],
        },
        {
            title: 'Par',
            key: 'par',
            width: 140,
            render: (_, r) => r.enregistrePar ? `${r.enregistrePar.firstName} ${r.enregistrePar.lastName}` : '—',
            responsive: ['lg'],
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 130,
            align: 'center',
            fixed: 'right',
            render: (_, r) => {
                const parentContrat = contrats.find((c) => c.reglements?.some((reg) => reg._id === r._id));
                return (
                    <div className="reglement-row-actions">
                        <Button
                            type="primary"
                            size="small"
                            icon={<PrinterOutlined />}
                            className="reglement-action-print"
                            onClick={() => printBordereau(r, parentContrat)}
                        >
                            Imprimer
                        </Button>
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'edit',
                                        icon: <EditOutlined />,
                                        label: 'Modifier',
                                        onClick: () => openEditModal(r),
                                    },
                                    { type: 'divider' },
                                    {
                                        key: 'delete',
                                        icon: <DeleteOutlined />,
                                        label: 'Supprimer',
                                        danger: true,
                                        onClick: () => handleDeleteReglement(r),
                                    },
                                ],
                            }}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<MoreOutlined />}
                                aria-label="Plus d'actions"
                                className="reglement-action-more"
                            />
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="detail-reglement-page">
            {/* Layout CRM : sidebar client (gauche) + content (droite) */}
            <div className="detail-reglement-layout">
                {/* ═════ SIDEBAR ═════ */}
                <div className="detail-reglement-sidebar">
                    <Card className="detail-reglement-sidebar-card">
                        {/* Bouton retour */}
                        <button
                            type="button"
                            className="sidebar-back-btn"
                            onClick={() => navigate('/dashboard/reglements')}
                            aria-label="Retour aux règlements"
                        >
                            <ArrowLeftOutlined />
                            <span>Retour aux règlements</span>
                        </button>

                        {/* Identité client */}
                        <div className="sidebar-identity">
                            <div className={`sidebar-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                                {isEntreprise ? <BankOutlined /> : <UserOutlined />}
                            </div>
                            <h1 className="sidebar-name">{clientName || '—'}</h1>
                            <div className="sidebar-meta">
                                <Tag color={isEntreprise ? 'orange' : 'blue'}>
                                    {isEntreprise ? 'Entreprise' : 'Particulier'}
                                </Tag>
                                {clientData.code && (
                                    <span className="sidebar-code">{clientData.code}</span>
                                )}
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        {/* Coordonnées */}
                        <div className="sidebar-coord">
                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><PhoneOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Téléphone</div>
                                    <div className="sidebar-coord-value">
                                        {clientData.telephone ? (
                                            <a href={`tel:${clientData.telephone}`} className="sidebar-coord-link">
                                                {formatPhone(clientData.telephone)}
                                            </a>
                                        ) : '—'}
                                    </div>
                                </div>
                            </div>

                            {clientData.agence && (
                                <div className="sidebar-coord-item">
                                    <div className="sidebar-coord-icon"><BankOutlined /></div>
                                    <div className="sidebar-coord-body">
                                        <div className="sidebar-coord-label">Agence</div>
                                        <div className="sidebar-coord-value">{clientData.agence.nom}</div>
                                    </div>
                                </div>
                            )}

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><FileProtectOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Contrats</div>
                                    <div className="sidebar-coord-value">
                                        {stats.nbContrats || 0} contrat{(stats.nbContrats || 0) > 1 ? 's' : ''}
                                    </div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><CheckCircleOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Paiements reçus</div>
                                    <div className="sidebar-coord-value">
                                        {stats.nbReglements || 0} paiement{(stats.nbReglements || 0) > 1 ? 's' : ''}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        {/* Action : fiche client */}
                        <div className="sidebar-actions">
                            <Button
                                icon={<EyeOutlined />}
                                onClick={() => navigate(`/dashboard/client/${clientData._id}`)}
                                className="sidebar-btn-client"
                                block
                            >
                                Voir la fiche client
                            </Button>
                        </div>
                    </Card>
                </div>

                {/* ═════ MAIN ═════ */}
                <div className="detail-reglement-main">
                    {/* KPIs */}
                    <section className="detail-reglement-kpis" aria-label="Indicateurs paiement">
                        <MiniKpiCard
                            icon={<FileProtectOutlined />}
                            label="Total primes"
                            value={formatMontant(stats.totalPrimeTTC || 0)}
                            suffix={stats.nbContrats ? `(${stats.nbContrats})` : ''}
                            tone="blue"
                        />
                        <MiniKpiCard
                            icon={<CheckCircleOutlined />}
                            label="Total payé"
                            value={formatMontant(stats.totalVerse || 0)}
                            suffix={stats.nbReglements ? `(${stats.nbReglements})` : ''}
                            tone="green"
                        />
                        <MiniKpiCard
                            icon={<ExclamationCircleOutlined />}
                            label="Reste à payer"
                            value={formatMontant(stats.totalResteAPayer || 0)}
                            tone={stats.totalResteAPayer > 0 ? 'red' : 'green'}
                        />
                        <MiniKpiCard
                            icon={<DollarOutlined />}
                            label="Commissions"
                            value={formatMontant(stats.totalCommissions || 0)}
                            tone="purple"
                        />
                        <MiniKpiCard
                            icon={<RiseOutlined />}
                            label="Progression"
                            value={`${stats.progressGlobal || 0}%`}
                            tone={stats.progressGlobal >= 100 ? 'green' : 'teal'}
                        />
                    </section>

                    {/* Liste des contrats */}
                    <Card className="detail-reglement-card">
                <div className="detail-reglement-card-title">
                    <FileProtectOutlined /> Contrats et paiements
                </div>

                {contrats.length === 0 ? (
                    <div className="detail-reglement-empty-state">Aucun contrat</div>
                ) : (
                    <Collapse
                        accordion
                        defaultActiveKey={contrats[0]?._id}
                        className="detail-reglement-collapse"
                        items={contrats.map((contrat) => {
                            const percent = contrat.progressPaiement || 0;
                            const status = STATUS_CONFIG(percent);
                            return {
                                key: contrat._id,
                                label: (
                                    <div className="contrat-panel-header">
                                        <div className="contrat-panel-head-left">
                                            <span className="contrat-numero">{contrat.numeroPolice || '—'}</span>
                                            {contrat.typeAssurance && (
                                                <Tag color="blue">{contrat.typeAssurance.nom}</Tag>
                                            )}
                                            {contrat.compagnie && (
                                                <span className="contrat-compagnie">{contrat.compagnie.nom}</span>
                                            )}
                                        </div>
                                        <div className="contrat-panel-head-right">
                                            <span className="contrat-prime">{formatMontant(contrat.primeTTC || 0)}</span>
                                            <Tag color={status.color}>{status.label}</Tag>
                                            <Progress
                                                percent={percent}
                                                size="small"
                                                strokeColor={percent >= 100 ? '#16a34a' : percent > 0 ? '#1A2B5F' : '#C8102E'}
                                                style={{ width: 80, margin: 0 }}
                                                format={(p) => `${p}%`}
                                            />
                                        </div>
                                    </div>
                                ),
                                children: (
                                    <div className="contrat-panel-body">
                                        {/* Mini-stats du contrat */}
                                        <div className="contrat-mini-stats">
                                            <div className="mini-stat">
                                                <div className="mini-stat-label">Prime TTC</div>
                                                <div className="mini-stat-value blue">{formatMontant(contrat.primeTTC || 0)}</div>
                                            </div>
                                            <div className="mini-stat">
                                                <div className="mini-stat-label">Total payé</div>
                                                <div className="mini-stat-value green">{formatMontant(contrat.totalVerse || 0)}</div>
                                            </div>
                                            <div className="mini-stat">
                                                <div className="mini-stat-label">Reste à payer</div>
                                                <div className={`mini-stat-value ${contrat.resteAPayer > 0 ? 'red' : 'green'}`}>
                                                    {formatMontant(Math.max(0, contrat.resteAPayer || 0))}
                                                </div>
                                            </div>
                                            <div className="mini-stat">
                                                <div className="mini-stat-label">Commission</div>
                                                <div className="mini-stat-value purple">{formatMontant(contrat.commissionCourtier || 0)}</div>
                                                {contrat.tauxCommission > 0 && (
                                                    <div className="mini-stat-sub">({contrat.tauxCommission}%)</div>
                                                )}
                                            </div>
                                            <div className="mini-stat">
                                                <div className="mini-stat-label">Période</div>
                                                <div className="mini-stat-value">
                                                    {contrat.dateEffet ? new Date(contrat.dateEffet).toLocaleDateString('fr-FR') : ''}
                                                    {' → '}
                                                    {contrat.dateEcheance ? new Date(contrat.dateEcheance).toLocaleDateString('fr-FR') : ''}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Toolbar paiements */}
                                        <div className="payments-toolbar">
                                            <span className="payments-title">
                                                Historique des paiements <strong>({contrat.reglements?.length || 0})</strong>
                                            </span>
                                            <Space>
                                                <Button
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => navigate(`/dashboard/contrat/${contrat._id}`)}
                                                    className="payments-btn-view"
                                                >
                                                    Voir contrat
                                                </Button>
                                                {contrat.resteAPayer > 0 && (
                                                    <Button
                                                        size="small"
                                                        type="primary"
                                                        icon={<PlusOutlined />}
                                                        onClick={() => openPaymentModal(contrat)}
                                                        className="payments-cta"
                                                    >
                                                        Effectuer un paiement
                                                    </Button>
                                                )}
                                            </Space>
                                        </div>

                                        {/* Tableau paiements */}
                                        {contrat.reglements && contrat.reglements.length > 0 ? (
                                            <Table
                                                columns={reglementColumns}
                                                dataSource={contrat.reglements}
                                                rowKey="_id"
                                                size="middle"
                                                pagination={false}
                                                scroll={{ x: 'max-content' }}
                                                className="reglements-history-table"
                                            />
                                        ) : (
                                            <div className="payments-empty">
                                                Aucun paiement enregistré pour ce contrat
                                            </div>
                                        )}
                                    </div>
                                ),
                            };
                        })}
                    />
                )}
            </Card>
                </div>
            </div>

            {/* ═══ MODAL PAIEMENT ═══ */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #52c41a, #73d13d)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                            <DollarOutlined />
                        </div>
                        <div>
                            <Text strong style={{ fontSize: 15 }}>Nouveau règlement</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>{clientName} — {selectedContrat?.numeroPolice}</Text>
                        </div>
                    </div>
                }
                open={modalVisible}
                onCancel={() => { setModalVisible(false); setSelectedContrat(null); form.resetFields(); }}
                footer={null}
                destroyOnClose
                width={560}
                style={{ top: 150 }}
            >
                {selectedContrat && (
                    <>
                        <Card size="small" style={{ background: '#f0f5ff', borderRadius: 10, marginBottom: 16, marginTop: 16, border: '1px solid #d6e4ff' }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Prime TTC</Text>
                                    <br />
                                    <Text strong style={{ color: '#1A2B5F', fontSize: 15 }}>{formatMontant(selectedContrat.primeTTC)}</Text>
                                </Col>
                                <Col span={8}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Déjà payé</Text>
                                    <br />
                                    <Text strong style={{ color: '#52c41a' }}>{formatMontant(selectedContrat.totalVerse)}</Text>
                                </Col>
                                <Col span={8}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Reste</Text>
                                    <br />
                                    <Text strong style={{ color: '#ff4d4f' }}>{formatMontant(Math.max(0, selectedContrat.resteAPayer))}</Text>
                                </Col>
                            </Row>
                        </Card>

                        <Form form={form} layout="vertical" onFinish={handleSubmitPayment}>
                            <Row gutter={12}>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="montant" label={<span><DollarOutlined style={{ marginRight: 6 }} />Montant</span>} rules={[{ required: true, message: 'Requis' }]}>
                                        <InputNumber
                                            min={0}
                                            style={{ width: '100%', borderRadius: 8 }}
                                            size="large"
                                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                            parser={(v) => v.replace(/\s/g, '')}
                                            placeholder="Montant en FCFA"
                                            addonAfter="FCFA"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="moyenPaiement" label={<span><WalletOutlined style={{ marginRight: 6 }} />Mode</span>} rules={[{ required: true, message: 'Requis' }]}>
                                        <Select placeholder="Sélectionner" size="large">
                                            <Option value="especes">Espèces</Option>
                                            <Option value="cheque">Chèque</Option>
                                            <Option value="virement">Virement</Option>
                                            <Option value="mobile_money">Mobile Money</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name="dateReglement" label={<span><CalendarOutlined style={{ marginRight: 6 }} />Date</span>} rules={[{ required: true, message: 'Requis' }]}>
                                <DatePicker style={{ width: '100%', borderRadius: 8 }} format="DD/MM/YYYY" size="large" />
                            </Form.Item>
                            <Row gutter={12}>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="reference" label="Référence" normalize={(v) => v ? v.toUpperCase() : v}>
                                        <Input placeholder="N. chèque, réf..." style={{ borderRadius: 8, textTransform: 'uppercase' }} size="large" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="banque" label="Banque" normalize={(v) => v ? v.toUpperCase() : v}>
                                        <Input placeholder="Nom de la banque" style={{ borderRadius: 8, textTransform: 'uppercase' }} size="large" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name="observation" label="Observation">
                                <Input.TextArea placeholder="Note..." rows={2} style={{ borderRadius: 8 }} />
                            </Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={modalLoading}
                                block
                                style={{
                                    borderRadius: 10,
                                    height: 46,
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                                    border: 'none',
                                    boxShadow: '0 4px 14px rgba(82, 196, 26, 0.35)',
                                }}
                            >
                                Enregistrer le règlement
                            </Button>
                        </Form>
                    </>
                )}
            </Modal>

            {/* ═══ MODAL ÉDITION ═══ */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #fa8c16, #ffa940)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                            <EditOutlined />
                        </div>
                        <div>
                            <Text strong style={{ fontSize: 15 }}>Modifier le règlement</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>Modifier les informations du paiement</Text>
                        </div>
                    </div>
                }
                open={editModalVisible}
                onCancel={() => { setEditModalVisible(false); setEditingReglement(null); editForm.resetFields(); }}
                footer={null}
                destroyOnClose
                width={560}
                style={{ top: 150 }}
            >
                <Divider style={{ margin: '16px 0' }} />
                <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
                    <Row gutter={12}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="montant" label={<span><DollarOutlined style={{ marginRight: 6 }} />Montant</span>} rules={[{ required: true, message: 'Requis' }]}>
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%', borderRadius: 8 }}
                                    size="large"
                                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={(v) => v.replace(/\s/g, '')}
                                    placeholder="Montant"
                                    addonAfter="FCFA"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="moyenPaiement" label={<span><WalletOutlined style={{ marginRight: 6 }} />Mode</span>} rules={[{ required: true, message: 'Requis' }]}>
                                <Select placeholder="Sélectionner" size="large">
                                    <Option value="especes">Espèces</Option>
                                    <Option value="cheque">Chèque</Option>
                                    <Option value="virement">Virement</Option>
                                    <Option value="mobile_money">Mobile Money</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="reference" label="Référence" normalize={(v) => v ? v.toUpperCase() : v}>
                                <Input placeholder="N. chèque, réf..." style={{ borderRadius: 8, textTransform: 'uppercase' }} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="banque" label="Banque" normalize={(v) => v ? v.toUpperCase() : v}>
                                <Input placeholder="Nom de la banque" style={{ borderRadius: 8, textTransform: 'uppercase' }} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="observation" label="Observation">
                        <Input.TextArea placeholder="Note..." rows={2} style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={editModalLoading}
                        block
                        style={{
                            borderRadius: 10,
                            height: 46,
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                            border: 'none',
                            boxShadow: '0 4px 14px rgba(250, 140, 22, 0.35)',
                        }}
                    >
                        Enregistrer les modifications
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};

export default DetailReglement;
